/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "cms/auth/hooks/use-auth";
import PaginationFooter from "components/pagination/pagination-footer";
import { Button, IconButton, InputAdornment, OutlinedInput } from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { LinkContext } from "cms/links/link-selector/context";
import { LinkDialogPreviewPanel } from "cms/links/link-selector/dialog-select/select/link-dialog-preview-panel";
import { Logger } from "cms/utils/log/logger";

const EMPTY_COUNT = 1;
const FIRST_PAGE = 1;
const DEFAULT_PAGES = 1;
const DEFAULT_PAGE_SIZE = 20;

const SearchFilter = (props) => {
    
    const {
        children,
        searchFunction,
        filterValue,
        autoLoad,
        needsRefresh,
        setNeedsRefresh,
        setItems
    } = props;
    
    const { onOpenCreate } = useContext(LinkContext);
    const { token } = useAuth();
    
    const [term, setTerm] = useState("");
    const [inputRef, setInputRef] = useState();
    const [loaded, setLoaded] = useState(false);
    // const [pagination, setPagination] = useState({});
    const [count, setCount] = useState(EMPTY_COUNT);
    const [page, setPage] = useState(FIRST_PAGE);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pages, setPages] = useState(DEFAULT_PAGES);

    const handleSearch = async (event) => {
        let _term;
        if (event) {
            _term = event.target.value;
        } else {
            _term = "";
        }
        setTerm(_term);
        const searchResults = await fetchContent(token, _term, filterValue, page, pageSize);
        const { results, pagination } = searchResults;
        setItems(results);
        setCount(pagination.total);
        setPage(pagination.page);
        setPages(pagination.pages);
        setPageSize(pagination.pageSize);
        // setPagination(pagination);
    };

    const fetchContent = useCallback(
        async (token, term, filterValue, page, pageSize) => {
            const options = { page, pageSize };
            if (!token) {
                throw Error("no auth token during search");
            }
            if (typeof searchFunction !== "function") {
                throw Error("searchFunction is not a Function."); 
            }
            const searchResults = await searchFunction(token, term, options, filterValue);
            const { results, pagination } = searchResults;
            setItems(results);
            setCount(pagination.total);
            setPage(pagination.page);
            setPages(pagination.pages);
            setPageSize(pagination.pageSize);
            // setPagination(pagination);
            return searchResults;
        },
        [searchFunction, setItems],
    );
    const selectPage = async (event, page) => {
        setPage(page);
        const searchTerm = term ? term : "ALL_RESULTS";
        const searchResults = await fetchContent(token, searchTerm, filterValue, page, pageSize);
        const { results, pagination } = searchResults;
        setItems(results);
        setCount(pagination.total);
        setPage(pagination.page);
        setPages(pagination.pages);
        setPageSize(pagination.pageSize);
        // setPagination(pagination);
    };
    const handlePageSizeChange = async (event, element) => {
        const size = element.props.value;
        let request_page = page;
        setPageSize(size);
        const expected_pages = count / size + (count % size > 0 ? 1 : 0);
        if (page > expected_pages) {
            setPage(FIRST_PAGE);
            request_page = FIRST_PAGE;
        }
        const searchTerm = term ? term : "ALL_RESULTS";
        const searchResults = await fetchContent(token, searchTerm, filterValue, request_page, size);
        const { results, pagination } = searchResults;
        setItems(results);
        setCount(pagination.total);
        setPage(pagination.page);
        setPages(pagination.pages);
        setPageSize(pagination.pageSize);
        // setPagination(pagination);
    };
    
    // To not search by default - comment the following
    useEffect(() => {
        if (!loaded) {
            if (autoLoad) {
                const runFetchContent = async function () {
                    const term = "ALL_RESULTS";
                    const searchResults = await fetchContent(token, term, filterValue, page, pageSize);
                    const { pagination, results } = searchResults;
                    setItems(results);
                    setCount(pagination.total);
                    setPage(pagination.page);
                    setPages(pagination.pages);
                    setPageSize(pagination.pageSize);
                    // setPagination(pagination);
                    // Logger.debug("loaded");
                    setLoaded(true);
                };
                runFetchContent(token, term, filterValue, page, pageSize);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetch = async () => {
            setTerm(term);
            const searchResults = await fetchContent(token, term, filterValue, page, pageSize);
            const results = searchResults?.results;
            const pagination = searchResults?.pagination;
            setItems(results);
            setCount(pagination.total);
            setPage(pagination.page);
            setPages(pagination.pages);
            setPageSize(pagination.pageSize);
            // setPagination(pagination);
            setNeedsRefresh(false);
            setLoaded(true);
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsRefresh]);
    
    const handleCreateLink = async () =>  {
        onOpenCreate();
    };
    const handleClear = async () =>  {
        // event.stopPropagation()
        setTerm("");
        await handleSearch();
        inputRef && inputRef.focus();
    };
    const hasTerm = !!term;
    
    return (
        <Fragment>
            <div className="simplicity-link-selector-search-filter-list-header">
                <div className="simplicity-link-selector-search-filter-list-searchbar">
                    <OutlinedInput
                        fullWidth
                        color='primary'
                        id='search-bar'
                        type='text'
                        value={term}
                        name='search-bar'
                        inputRef={(input) => input && setInputRef(input)}
                        onChange={handleSearch.bind(this)}
                        startAdornment={
                            <InputAdornment position='start'>
                                <Search />
                            </InputAdornment>
                        }
                        endAdornment={
                            <>
                                {hasTerm &&
                                    <IconButton
                                        aria-label="clear link"
                                        onClick={(event) => handleClear(event)}
                                        edge="end"
                                        size='small'
                                        sx={{ width: "32px" }}
                                    >
                                        <InputAdornment position='end'>
                                            <Close />
                                        </InputAdornment>
                                    </IconButton>
                                }
                            </>
                        }
                        placeholder='Search link'
                    />
                </div>
                <div className="simplicity-link-selector-search-filter-list-search-button">
                    <Button
                        variant='contained'
                        color='primary'
                        style={{ width: "90%", height: "41px" }}
                        startIcon={<AddIcon color='secondary'/>}
                        onClick={(event) => handleCreateLink(event)}
                    >
                        Create New Link
                    </Button>
                </div>
            </div>
            <div className='simplicity-link-selector-search-filter-list-instruction'>
                Recent Links
            </div>
            
            
            {/* ----------------------------- Main Body of Gallery Window -------------------------- */}
            
            {/*  center/children - expands to take up space  */}
            <div className="simplicity-link-selector-search-filter-list">
                <div
                    className="simplicity-link-selector-window-container"
                    style={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "row",
                        // border: '1px solid blue',
                    }}
                >
                    {/*  search results  */}
                    <div className="simplicity-link-selector-window-list">
                        {children}
                    </div>
                    {/*  preview  */}
                    <div className="simplicity-link-selector-window-selected">
                        <LinkDialogPreviewPanel />
                    </div>
                </div>
            </div>
            
            <div style={{ flex: "0 1 50px" }}>  {/* px is an offset equal to the top bar */}
                <PaginationFooter
                    page={page}
                    pageSize={pageSize}
                    pages={pages}
                    handlePageSizeChange={handlePageSizeChange.bind(this)}
                    selectPage={selectPage.bind(this)}
                />
            </div>
        </Fragment>
    );
};

export default SearchFilter;
